import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ProfileProviderPJ, ProfileProviderPjControllerService } from '../../core/generated';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProfileProviderPJStateService {
  private profileProviderPJService = inject(ProfileProviderPjControllerService);
  private profileProviderPJSubject = new BehaviorSubject<ProfileProviderPJ[] | null>(null);

  // Prevent duplicate network calls for the same email
  private lastEmailRequested: string | null = null;
  private inFlightLoads = new Map<string, Promise<void>>();

  get profileProviderPJ$(): Observable<ProfileProviderPJ[] | null> {
    return this.profileProviderPJSubject.asObservable();
  }

  loadByEmail(email: string) {
    if (!email) return;

    // If we already have data for this email, skip
    if (this.lastEmailRequested === email && this.profileProviderPJSubject.value !== null) {
      return;
    }

    this._fetchByEmail(email);
  }

  /** Força o recarregamento ignorando o cache — use após criar/remover perfis */
  forceRefreshByEmail(email: string) {
    if (!email) return;
    this.lastEmailRequested = null; // reseta o guard
    this.inFlightLoads.delete(email);
    this._fetchByEmail(email);
  }

  private _fetchByEmail(email: string) {
    // If there's already an in-flight request for this email, don't start another
    if (this.inFlightLoads.has(email)) {
      return;
    }

    this.lastEmailRequested = email;

    const p = firstValueFrom(this.profileProviderPJService.findByUserEmail(email))
      .then((profiles) => {
        this.profileProviderPJSubject.next(profiles || []);
      })
      .catch((err) => {
        console.error('ProfileProviderPJStateService: erro ao carregar perfis para', email, err);
        this.profileProviderPJSubject.next([]);
      })
      .finally(() => {
        this.inFlightLoads.delete(email);
      });

    this.inFlightLoads.set(email, p);
  }

  setProfiles(profiles: ProfileProviderPJ[]) {
    this.profileProviderPJSubject.next(profiles);
  }

  addProfile(profile: ProfileProviderPJ) {
    const currentProfiles = this.profileProviderPJSubject.value || [];
    this.profileProviderPJSubject.next([...currentProfiles, profile]);
  }

  updateProfile(updatedProfile: ProfileProviderPJ) {
    const currentProfiles = this.profileProviderPJSubject.value || [];
    const updatedProfiles = currentProfiles.map(profile =>
      profile.id === updatedProfile.id ? updatedProfile : profile
    );
    this.profileProviderPJSubject.next(updatedProfiles);
  }

  removeProfile(profileId: number) {
    const currentProfiles = this.profileProviderPJSubject.value || [];
    this.profileProviderPJSubject.next(currentProfiles.filter(profile => profile.id !== profileId));
  }

  refreshByEmail(email: string) {
    this.forceRefreshByEmail(email);
  }

  clear() {
    this.lastEmailRequested = null;
    this.profileProviderPJSubject.next(null);
  }
}
